import { Badge } from '@/components/core/Badge';
import { ToggleGroup } from '@/components/core/ToggleGroup/ToggleGroup';
import { ToggleGroupItem } from '@/components/core/ToggleGroup/ToggleGroupItem';
import { cn } from '@/services/utils';

interface DamUploadTab<T extends string> {
    value: T;
    label: string;
    /** `undefined` shows "…" while uploads are running. */
    count: number | undefined;
}

interface DamUploadTabsProps<T extends string> {
    tabs: DamUploadTab<T>[];
    value: T;
    onChange: (value: T) => void;
    label: string;
}

/**
 * Segmented tabs with counters of the upload wizard (wireframe `7328:237416;7510:264895`,
 * `7537:57862;7510:266634`, `7328:237429;7510:267452`): each tab filters the file list.
 */
function DamUploadTabs<T extends string>({ tabs, value, onChange, label }: DamUploadTabsProps<T>) {
    return (
        <ToggleGroup
            type="single"
            value={value}
            onValueChange={(next) => next && onChange(next as T)}
            aria-label={label}
            className="tw-flex tw-w-fit tw-max-w-full tw-overflow-x-auto tw-rounded-md tw-border tw-border-solid tw-border-primary"
        >
            {tabs.map((tab) => (
                <ToggleGroupItem
                    key={tab.value}
                    value={tab.value}
                    className={cn(
                        'tw-mr-0 tw-shrink-0 tw-gap-2 tw-rounded-none tw-px-4 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base',
                        'tw-border-y-0 tw-border-l-0 tw-border-r tw-border-solid tw-border-primary last:tw-border-r-0',
                        'data-[state=on]:tw-bg-fill-tertiary data-[state=on]:tw-font-bold',
                    )}
                >
                    {tab.label}
                    <Badge variant="neutral">{tab.count === undefined ? '…' : tab.count.toLocaleString()}</Badge>
                </ToggleGroupItem>
            ))}
        </ToggleGroup>
    );
}

export { DamUploadTabs };
export type { DamUploadTab, DamUploadTabsProps };
